import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useWordOfDay } from '@/hooks/useWordOfDay';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import DetailPageLayout from '@/components/DetailPageLayout';
import DetailPageHeader from '@/components/DetailPageHeader';
import type { Language } from '@/types';

// Part of speech labels in all languages
const PART_OF_SPEECH_LABELS: Record<string, Record<Language, string>> = {
  noun: { en: 'noun', ru: 'существительное', pl: 'rzeczownik' },
  verb: { en: 'verb', ru: 'глагол', pl: 'czasownik' },
  adjective: { en: 'adjective', ru: 'прилагательное', pl: 'przymiotnik' },
  adverb: { en: 'adverb', ru: 'наречие', pl: 'przysłówek' },
  pronoun: { en: 'pronoun', ru: 'местоимение', pl: 'zaimek' },
  preposition: { en: 'preposition', ru: 'предлог', pl: 'przyimek' },
  conjunction: { en: 'conjunction', ru: 'союз', pl: 'spójnik' },
  interjection: { en: 'interjection', ru: 'междометие', pl: 'wykrzyknik' },
  phrase: { en: 'phrase', ru: 'фраза', pl: 'fraza' },
};

// Gender labels in all languages
const GENDER_LABELS: Record<string, Record<Language, string>> = {
  masculine: { en: 'masculine', ru: 'мужской', pl: 'rodzaj męski' },
  feminine: { en: 'feminine', ru: 'женский', pl: 'rodzaj żeński' },
  neuter: { en: 'neuter', ru: 'средний', pl: 'rodzaj nijaki' },
};

export default function WordOfDayPage() {
  const { language } = useAppLanguage();
  const { data: word, isLoading, isError } = useWordOfDay(language);

  const getPartOfSpeechLabel = (pos: string): string => {
    return PART_OF_SPEECH_LABELS[pos]?.[language] || pos;
  };

  const getGenderLabel = (gender: string | null): string | null => {
    if (!gender) return null;
    return GENDER_LABELS[gender]?.[language] || gender;
  };

  if (isLoading) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('wordOfDay', language)} />
        <div style={styles.content}>
          <div style={styles.loadingMessage}>{getTranslation('loading', language)}</div>
        </div>
      </DetailPageLayout>
    );
  }

  if (isError || !word) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('wordOfDay', language)} />
        <div style={styles.content}>
          <div style={styles.errorMessage}>{getTranslation('error', language)}</div>
        </div>
      </DetailPageLayout>
    );
  }

  const genderLabel = getGenderLabel(word.gender);

  return (
    <DetailPageLayout>
      <DetailPageHeader title={getTranslation('wordOfDay', language)} />

      <div style={styles.content}>
        {/* Main word card */}
        <div style={styles.mainCard}>
          {/* Icelandic word */}
          <div style={styles.wordContainer}>
            <h2 style={styles.icelandicWord}>{word.icelandic}</h2>
            <div style={styles.ipaContainer}>
              <span style={styles.ipa}>{word.ipa}</span>
            </div>
          </div>

          {/* Badges: part of speech + gender */}
          <div style={styles.badgesContainer}>
            <span style={styles.badge}>{getPartOfSpeechLabel(word.partOfSpeech)}</span>
            {genderLabel && (
              <span style={styles.badgeGender}>{genderLabel}</span>
            )}
          </div>

          {/* Translation */}
          <div style={styles.translationContainer}>
            <h3 style={styles.translationLabel}>{getTranslation('translation', language)}</h3>
            <p style={styles.translation}>{word.translation}</p>
            {word.notes && <p style={styles.notes}>{word.notes}</p>}
          </div>
        </div>

        {/* Examples section */}
        {word.examples && word.examples.length > 0 && (
          <div style={styles.examplesSection}>
            <h3 style={styles.sectionTitle}>{getTranslation('examples', language)}</h3>
            {word.examples.map((example, index) => (
              <div key={index} style={styles.exampleCard}>
                <p style={styles.exampleIcelandic}>{example.icelandic}</p>
                <p style={styles.exampleTranslation}>{example.translation}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Spacing for bottom nav */}
      <div style={{ height: '40px' }} />
    </DetailPageLayout>
  );
}

const styles = {
  content: {
    padding: '24px',
  } as React.CSSProperties,

  loadingMessage: {
    padding: '24px',
    textAlign: 'center' as const,
    color: Colors.textSecondary,
    fontSize: '16px',
  } as React.CSSProperties,

  errorMessage: {
    padding: '24px',
    textAlign: 'center' as const,
    color: Colors.error,
    fontSize: '16px',
  } as React.CSSProperties,

  mainCard: {
    backgroundColor: Colors.surface,
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    marginBottom: '24px',
  } as React.CSSProperties,

  wordContainer: {
    textAlign: 'center' as const,
    marginBottom: '16px',
  } as React.CSSProperties,

  icelandicWord: {
    fontSize: '36px',
    fontWeight: '700',
    color: Colors.primary,
    margin: 0,
  } as React.CSSProperties,

  ipaContainer: {
    marginTop: '8px',
  } as React.CSSProperties,

  ipa: {
    fontSize: '18px',
    color: Colors.textSecondary,
    fontFamily: 'serif',
  } as React.CSSProperties,

  badgesContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '24px',
  } as React.CSSProperties,

  badge: {
    padding: '4px 12px',
    backgroundColor: Colors.ice,
    color: Colors.primaryDark,
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  } as React.CSSProperties,

  badgeGender: {
    padding: '4px 12px',
    backgroundColor: Colors.primaryLight,
    color: Colors.primaryDark,
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  } as React.CSSProperties,

  translationContainer: {
    textAlign: 'center' as const,
  } as React.CSSProperties,

  translationLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: Colors.textSecondary,
    textTransform: 'uppercase' as const,
    margin: '0 0 8px 0',
  } as React.CSSProperties,

  translation: {
    fontSize: '24px',
    fontWeight: '600',
    color: Colors.text,
    margin: 0,
  } as React.CSSProperties,

  notes: {
    fontSize: '14px',
    color: Colors.textSecondary,
    fontStyle: 'italic' as const,
    marginTop: '8px',
    margin: '8px 0 0 0',
  } as React.CSSProperties,

  examplesSection: {
    marginTop: '24px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: Colors.text,
    margin: '0 0 16px 0',
  } as React.CSSProperties,

  exampleCard: {
    backgroundColor: Colors.surface,
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
  } as React.CSSProperties,

  exampleIcelandic: {
    fontSize: '16px',
    fontWeight: '600',
    color: Colors.text,
    margin: '0 0 4px 0',
  } as React.CSSProperties,

  exampleTranslation: {
    fontSize: '14px',
    color: Colors.textSecondary,
    margin: 0,
  } as React.CSSProperties,
} as const;
